// The phone tier's nav chrome (BL-1439) — a slim bar carrying the hamburger
// that opens the shell's rail as a left drawer. One bar for all three shells:
// the dashboard (BL-1439) and the Settings / Super Admin take-overs (BL-1647).
//
// This is NOT a second nav. The drawer's contents are the same rail the
// desktop shell docks, in its drawer variant: `<Sidebar>` on the dashboard,
// `<TakeOverRail>` / `<SuperAdminRail>` on the take-overs — same nav arrays,
// same order, same gating. That is the governing principle of PRJ-57 — every
// mobile surface is the shipped admin page restacked, never a parallel
// mobile-only UI — and it is why this file holds no nav of its own.
//
// WHO DECIDES THE TIER: CSS does, not this component. Each shell renders this
// bar behind `md:hidden` and hides its docked rail below 768px (Tailwind's
// `hidden md:block` on the dashboard; the 767px tier of `settings-v2.css` on
// the take-overs), so the switch is correct from the first byte at any width.
// A JS-gated layout would paint the rail for one frame at phone width and then
// snap, because `useMediaQuery`'s server snapshot is `false` and only
// self-corrects after hydration — the hook's own docs warn about exactly that.
// The one thing CSS cannot do is close an ALREADY-OPEN drawer when the viewport
// grows, because the drawer portals to `document.body` and is therefore outside
// this bar's `md:hidden`. That single job is what the media query below is for.
//
// BUG-2093 — the bar carries no Inbox icon. The drawer's Inbox item, with its
// live unread count, is the one way in (it reversed BL-2491's top-bar @).

'use client';

import { Suspense, lazy, useCallback, useId, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Menu } from 'lucide-react';
import { clsx } from 'clsx';
import { AuctogonLogo, useMediaQuery } from '@auctogon/ui/web';

import Sidebar from './Sidebar';
import Button from './ui/Button';
import { SlideOutPanel } from './ui/SlideOutPanel';
import { SHELL_ROW_HEIGHT } from './shell-geometry';
import { SIDEBAR_PINNED_VIEWPORT_QUERY } from '@auctogon/admin-isomorphic/isomorphic/constants/sidebar';
import { DARK_BAR_ICON_BUTTON, DARK_BAR_LEADING_PULL } from './sidebar/dark-bar-icon-button';

/** Bar height's lockup, a touch smaller than the rail's 24px. */
const BAR_LOCKUP_HEIGHT = 20;


// The take-over rails load only when a take-over shell opens its drawer. Every
// dashboard page mounts this bar, and a static import would ship both rails and
// their nav data in the dashboard chunk for a drawer that never holds them. On
// the take-over pages the shell has already imported its own rail, so the
// chunk resolves from cache on first open.
const TakeOverRail = lazy(() => import('./settings-v2/TakeOverRail'));
const SuperAdminRail = lazy(() => import('./settings-v2/SuperAdminRail'));

/**
 * Which shell hosts the bar. A surface name, not a domain value (ADR 029 —
 * View Units): it picks the bar's chrome and which rail the drawer holds; the
 * rail still sources its own nav, tenant and permissions.
 */
export type MobileNavBarVariant = 'dashboard' | 'settings' | 'super-admin';

/** The drawer's accessible name — the name of the rail it holds. */
const DRAWER_LABEL: Record<MobileNavBarVariant, string> = {
    dashboard: 'Main navigation',
    settings: 'Settings navigation',
    'super-admin': 'Super Admin navigation',
};

/** The take-over bar's section name — the same words the rail's own head shows. */
const TAKEOVER_TITLE: Record<Exclude<MobileNavBarVariant, 'dashboard'>, string> = {
    settings: 'Settings',
    'super-admin': 'Super Admin',
};

interface MobileNavBarProps {
    /** Visibility class from the shell — `md:hidden`. */
    className?: string;
    /** Which shell hosts the bar. Defaults to `dashboard`. */
    variant?: MobileNavBarVariant;
}

/**
 * The phone-tier top bar and its nav drawer.
 *
 * Self-contained: it owns the drawer's open state and takes no domain values,
 * only the shell's visibility class and which shell it is
 * (`self-contained-view-units.md`, Cluster 3).
 *
 * @param props - see {@link MobileNavBarProps}
 * @returns The bar, plus the drawer once it has been opened.
 */
export default function MobileNavBar({ className, variant = 'dashboard' }: MobileNavBarProps) {
    const [isOpen, setIsOpen] = useState(false);
    const drawerId = useId();
    const isPinnedTier = useMediaQuery(SIDEBAR_PINNED_VIEWPORT_QUERY);
    const pathname = usePathname();
    const isDashboard = variant === 'dashboard';

    // Derived state during render, NOT a `setState` in an effect: the effect
    // form trips `react-hooks/set-state-in-effect` and costs an extra commit.
    // Same pattern `SidebarUserMenu` uses to close its dropdown on collapse.
    //
    // Without this, rotating a phone to landscape (or dragging a desktop window
    // narrow and back) leaves a full-height drawer stranded over a page that has
    // already re-grown its pinned rail.
    const [wasPinnedTier, setWasPinnedTier] = useState(isPinnedTier);
    if (isPinnedTier !== wasPinnedTier) {
        setWasPinnedTier(isPinnedTier);
        if (isPinnedTier) setIsOpen(false);
    }

    // BL-1647 — a drawer never outlives the page it was opened on. A take-over
    // leaf is a `next/link`, and the unsaved-changes guard intercepts anchor
    // clicks in the CAPTURE phase and stops propagation, so the leaf's own
    // close never runs; the route changes later, from the guard's confirm.
    const [openedOnPath, setOpenedOnPath] = useState(pathname);
    if (pathname !== openedOnPath) {
        setOpenedOnPath(pathname);
        setIsOpen(false);
    }

    const close = useCallback(() => setIsOpen(false), []);

    return (
        <>
            {/* 56px (BL-2553, the shared phone header row) and a 16px gutter, matched to the drawer header this bar
                opens: the two are the same chrome seen from either side of a
                tap, so a height or gutter that differs between them reads as
                the page jumping. 16 is also the page gutter at this tier
                (BL-1647), so the hamburger's left edge and the content below it
                sit on one line — `shell-gutter-pairing.test.ts` pins the `px-4`.
                The take-over bar is the rail's light surface (`.toverail-bar`,
                contrast measured beside it in settings-v2.css), not the
                dashboard's dark brand bar. */}
            <header
                data-testid="mobile-nav-bar"
                className={clsx(
                    'flex shrink-0 items-center gap-density-control px-4',
                    SHELL_ROW_HEIGHT,
                    isDashboard ? 'border-b border-sidebar-border bg-sidebar' : 'toverail-bar',
                    className,
                )}
            >
                <Button
                    variant={isDashboard ? 'ondark' : 'ghost'}
                    size="icon"
                    // `size="icon"` alone is `p-2` — 40px around a 24px glyph,
                    // under the 44px touch floor BL-1411 set on this same rail's
                    // user menu. On a phone this button is the ONLY route to
                    // every page other than the one you are on.
                    //
                    // On the dark bar, `ondark`'s fill and border are both
                    // dropped. They exist to say "this is a button", which a
                    // hamburger alone in a header does not need — it is the most
                    // recognised control in mobile UI and the only interactive
                    // thing on its side of the bar. Same call bidder-web's
                    // `IconButton` variant `plain` makes: nothing at rest, a wash
                    // on interaction. The variant keeps its box for the clerk
                    // console's command bar, where the buttons are labelled and
                    // are the primary controls. `ghost` is already chrome-less.
                    //
                    // `hover:` is safe to write plainly because the config now
                    // sets `hoverOnlyWhenSupported`, which wraps every hover in
                    // `@media (hover: hover)`. Without that flag a tap triggers
                    // the hover and it STICKS — the same defect BUG-1532 fixed on
                    // the focus ring, arriving by the other door. `enabled:active:` is
                    // what a finger gets, and the only state that answers a press.
                    // BL-2553: `-ml-2.5` pulls the 44px target's 10px inset out so the
                    // 24px glyph's left edge sits on the 16px gutter, over the title.
                    className={clsx(DARK_BAR_LEADING_PULL, isDashboard ? DARK_BAR_ICON_BUTTON : 'min-h-11 min-w-11')}
                    aria-label="Open navigation"
                    aria-expanded={isOpen}
                    aria-controls={drawerId}
                    data-testid="mobile-nav-toggle"
                    onClick={() => setIsOpen(true)}
                >
                    <Menu className="h-6 w-6" aria-hidden />
                </Button>
                {isDashboard ? (
                    <AuctogonLogo
                        variant="lockup"
                        height={BAR_LOCKUP_HEIGHT}
                        title="Auctogon"
                        className="text-secondary flex-shrink-0"
                    />
                ) : (
                    <span className="toverail__title">{TAKEOVER_TITLE[variant]}</span>
                )}
            </header>

            {/* `lazy` is load-bearing (BL-1439): without it the rail would
                mount on every page at every viewport, putting a second nav tree
                — tenant selector, profile modal, permission reads — in the DOM
                behind the desktop rail. The panel supplies the portal, scrim,
                backdrop-click dismissal, Escape (topmost-only), focus trap and
                body-scroll lock, so none of that is hand-rolled here
                (`ui-standards.md` → overlays). It also derives its own layer, so
                no z-index is stated. */}
            <SlideOutPanel
                isOpen={isOpen}
                onClose={close}
                side="left"
                lazy
                width="max-w-nav-drawer"
                ariaLabel={DRAWER_LABEL[variant]}
                testId="mobile-nav-drawer"
            >
                <div id={drawerId} className="h-full w-full">
                    <DrawerRail variant={variant} onRequestClose={close} />
                </div>
            </SlideOutPanel>
        </>
    );
}

/**
 * The rail the drawer holds — the host shell's own rail, in its drawer variant.
 *
 * @param props.variant - Which shell hosts the bar.
 * @param props.onRequestClose - Closes the drawer.
 * @returns The rail element.
 */
function DrawerRail({ variant, onRequestClose }: { variant: MobileNavBarVariant; onRequestClose: () => void }) {
    if (variant === 'dashboard') return <Sidebar variant="drawer" onRequestClose={onRequestClose} />;
    return (
        <Suspense fallback={null}>
            {variant === 'settings' ? (
                <TakeOverRail variant="drawer" onRequestClose={onRequestClose} />
            ) : (
                <SuperAdminRail variant="drawer" onRequestClose={onRequestClose} />
            )}
        </Suspense>
    );
}
